import { attributes, html, joinClasses } from 'BomdisoftComponents/html/index';

export type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular';

export type SkeletonOptions = {
  className?: string;
  height?: number | string;
  variant?: SkeletonVariant;
  width?: number | string;
};

export type ChipSkeletonOptions = {
  height?: number;
  variant?: 'circular' | 'rectangular' | 'rounded';
  width?: number;
};

export type ContainerSkeletonOptions = {
  numberOfBlocks?: number;
  numberOfFilters?: number;
  showFilterBar?: boolean;
};

export type DataGridSkeletonOptions = {
  columns?: number;
  rows?: number;
};

export type DocSkeletonOptions = {
  numberOfParagraphs?: number;
  rowsPerParagraph?: number;
  showTitle?: boolean;
};

const toSize = (value?: number | string) => (typeof value === 'number' ? `${value}px` : value);

const repeat = <T>(count: number, render: (index: number) => T) => Array.from({ length: count }, (_, index) => render(index));

export const renderSkeleton = ({ className, height, variant = 'text', width }: SkeletonOptions = {}) => {
  const declarations = [toSize(width) ? `--sk-w: ${toSize(width)}` : '', toSize(height) ? `--sk-h: ${toSize(height)}` : ''].filter(declaration => declaration !== '');
  return html`<span${attributes({ 'aria-hidden': 'true', class: joinClasses('bomdisoft-skeleton', className), 'data-variant': variant, style: declarations.length > 0 ? declarations.join('; ') : undefined })}></span>`;
};

export const renderChipSkeleton = ({ height = 32, variant = 'rounded', width = 100 }: ChipSkeletonOptions = {}) => renderSkeleton({ className: 'bomdisoft-skeleton-chip', height, variant, width });

export const renderContainerSkeleton = ({ numberOfBlocks = 6, numberOfFilters = 6, showFilterBar = true }: ContainerSkeletonOptions = {}) =>
  html`<div class="bomdisoft-skeleton-container">${showFilterBar ? html`<div class="bomdisoft-skeleton-filter-bar">${renderSkeleton({ className: 'bomdisoft-skeleton-search-bar', height: 35, variant: 'rectangular', width: '20%' })}<div class="bomdisoft-skeleton-filters">${repeat(numberOfFilters, () => renderSkeleton({ className: 'bomdisoft-skeleton-filter', height: 35, variant: 'rectangular', width: 100 }))}</div></div>` : ''}${repeat(numberOfBlocks, () => renderSkeleton({ className: 'bomdisoft-skeleton-block', height: 140, variant: 'rectangular', width: '100%' }))}</div>`;

export const renderDataGridSkeleton = ({ columns = 5, rows = 10 }: DataGridSkeletonOptions = {}) =>
  html`<div class="bomdisoft-skeleton-grid"><div class="bomdisoft-skeleton-grid-row bomdisoft-skeleton-grid-header">${repeat(columns, () => renderSkeleton({ className: 'bomdisoft-skeleton-grid-header-cell', height: 40, variant: 'rectangular' }))}</div>${repeat(rows, () => html`<div class="bomdisoft-skeleton-grid-row">${repeat(columns, () => renderSkeleton({ className: 'bomdisoft-skeleton-grid-cell', height: 52, variant: 'rectangular' }))}</div>`)}</div>`;

export const renderDocSkeleton = ({ numberOfParagraphs = 3, rowsPerParagraph = 6, showTitle = true }: DocSkeletonOptions = {}) =>
  html`${showTitle ? renderSkeleton({ className: 'bomdisoft-skeleton-doc-title', height: 40, variant: 'text', width: '20%' }) : ''}${repeat(numberOfParagraphs, () => repeat(rowsPerParagraph, index => renderSkeleton({ className: index === rowsPerParagraph - 1 ? 'bomdisoft-skeleton-doc-row-last' : 'bomdisoft-skeleton-doc-row', height: 20, variant: 'text', width: '85%' })))}`;

export const renderStatCardSkeleton = () =>
  html`<div class="bomdisoft-skeleton-stat-card"><div class="bomdisoft-skeleton-stat-card-content"><div class="bomdisoft-skeleton-stat-card-count">${renderSkeleton({ height: 24, variant: 'circular', width: 24 })}${renderSkeleton({ height: 40, variant: 'text', width: 60 })}</div>${renderSkeleton({ height: 1, variant: 'rectangular', width: '100%' })}${renderSkeleton({ height: 24, variant: 'text', width: 80 })}</div></div>`;
